import type { Session } from "@supabase/supabase-js";
import { useCallback, useEffect, useState } from "react";
import { adminRequest, type InvitationRow } from "../adminApi";
import { LoadingPanel } from "../components/LoadingPanel";
import { Pagination } from "../components/Pagination";
import { Status } from "../components/Status";

const PAGE_SIZE = 50;

export function InvitationsPage({
  mfaToken,
  session,
  setNotice
}: {
  mfaToken: string | null;
  session: Session;
  setNotice(n: { tone: "error" | "success"; text: string } | null): void;
}) {
  const [invitations, setInvitations] = useState<InvitationRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteLabel, setInviteLabel] = useState("");
  const [busyInvite, setBusyInvite] = useState(false);

  const loadPage = useCallback(async (p: number) => {
    setLoading(true);
    try {
      const offset = (p - 1) * PAGE_SIZE;
      const data = await adminRequest<{ invitations: InvitationRow[]; total_count: number }>(
        `/invitations?offset=${offset}&limit=${PAGE_SIZE}`,
        { mfaToken, session }
      );
      setInvitations(data.invitations);
      setTotalCount(data.total_count);
      setPage(p);
    } catch (caught) {
      setNotice({ tone: "error", text: caught instanceof Error ? caught.message : "Could not load invitations." });
    } finally {
      setLoading(false);
    }
  }, [mfaToken, session, setNotice]);

  useEffect(() => { void loadPage(1); }, []);

  async function invite(event: React.FormEvent) {
    event.preventDefault();
    setBusyInvite(true);
    try {
      await adminRequest<{ invitation_id: string }>("/invitations", {
        body: { device_label: inviteLabel || null, email: inviteEmail },
        method: "POST",
        mfaToken,
        session
      });
      setInviteEmail("");
      setInviteLabel("");
      setNotice({ tone: "success", text: "Invitation created and email sent." });
      await loadPage(page);
    } catch (caught) {
      setNotice({ tone: "error", text: caught instanceof Error ? caught.message : "Could not create invitation." });
    } finally {
      setBusyInvite(false);
    }
  }

  async function invitationAction(id: string, action: "resend" | "revoke") {
    try {
      await adminRequest(`/invitations/${id}/${action}`, { method: "POST", mfaToken, session });
      setNotice({ tone: "success", text: action === "resend" ? "Invitation resent." : "Invitation revoked." });
      await loadPage(page);
    } catch (caught) {
      setNotice({ tone: "error", text: caught instanceof Error ? caught.message : `Could not ${action} invitation.` });
    }
  }

  return (
    <div className="stack">
      <section className="section">
        <div className="sectionHead">
          <div>
            <h1>Invitations</h1>
            <p>Create invite-only access and track redemption.</p>
          </div>
        </div>
        <form className="inviteForm" onSubmit={(event) => void invite(event)}>
          <label>
            Email
            <input inputMode="email" onChange={(event) => setInviteEmail(event.target.value)} required type="email" value={inviteEmail} />
          </label>
          <label>
            Note
            <input onChange={(event) => setInviteLabel(event.target.value)} placeholder="Optional source or note" value={inviteLabel} />
          </label>
          <button disabled={busyInvite} type="submit">{busyInvite ? "Inviting…" : "Invite user"}</button>
        </form>
        {loading ? <LoadingPanel label="Loading invitations…" /> : (
          <InvitationTable invitations={invitations} onAction={invitationAction} />
        )}
        <Pagination currentPage={page} pageSize={PAGE_SIZE} totalCount={totalCount} onPageChange={loadPage} />
      </section>
    </div>
  );
}

function InvitationTable({
  invitations,
  onAction
}: {
  invitations: InvitationRow[];
  onAction(id: string, action: "resend" | "revoke"): Promise<void>;
}) {
  return (
    <div className="tableWrap">
      <table>
        <thead>
          <tr>
            <th>Email</th>
            <th>Status</th>
            <th>Invited</th>
            <th>Expires</th>
            <th>Accepted</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {invitations.map((invite) => (
            <tr key={invite.id}>
              <td>
                <strong>{invite.email}</strong>
                {invite.device_label ? <span>{invite.device_label}</span> : null}
              </td>
              <td><Status value={invite.status} /></td>
              <td>{formatDate(invite.created_at)}</td>
              <td>{formatDate(invite.expires_at)}</td>
              <td>{invite.accepted_at ? formatDate(invite.accepted_at) : "—"}</td>
              <td>
                {invite.status === "pending" ? (
                  <div className="rowActions">
                    <button onClick={() => void onAction(invite.id, "resend")} type="button">Resend</button>
                    <button className="danger" onClick={() => void onAction(invite.id, "revoke")} type="button">Revoke</button>
                  </div>
                ) : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!invitations.length ? <div className="empty">No invitations yet.</div> : null}
    </div>
  );
}

function formatDate(value: string) {
  return new Date(value).toLocaleString();
}
